// router/index.tsx — React Router 7 data-router per ADR-021.
//
// Three trust patterns (ANATOMY §Three traversal patterns):
//   pre-auth        — /login, /change-password (no ProtectedRoute)
//   authed
//   project-less    — /, /profile, /platform/* (reserved) (full shell:
//                     AuthedLayout) · /not-found (no layout)
//   authed
//   slim            — /select-project (lockup + full avatar), /project-blocked
//                     (switcher + full avatar), /no-projects (lockup + trimmed
//                     avatar) — SlimAuthedLayout: no operational tools
//   authed
//   project-scoped  — /p/:projectId/* (ProtectedRoute + project context)
//
// Shell layout nesting (the reconcile decision — top bar single-sourced in AuthedLayout):
//   AuthedLayout owns the top bar (Navbar) + the single <main> landmark; the
//   project subtree splits into two PATHLESS frame layouts —
//     ProjectLayout  → the domain frame (home + monitoring/sites/assets/...)
//     AdminLayout    → the admin frame (section sidebar) at FLAT URLs (no
//                      /admin/ segment per ADR-018; "admin mode" is a layout).
//   Both partition /p/:projectId by which left region the page needs.
//
// SlimAuthedLayout is a sibling frame of AuthedLayout for authed pages that drop
// the operational tools (the chooser + terminal pages); its brand/account slots
// are set per route. A fully focused/bare route (no top bar at all) would mount
// the same way — as a sibling — exactly as /login does today.
//
// Lazy/eager (split-by-weight, ADR-021 + FRONTEND-PATTERNS §Lazy loading):
//   - The shell frame + the ComingSoon stubs stay EAGER (tiny — nothing to
//     defer). The `m-<area>` boundaries (vite.config.ts) are the documented
//     convention for when a heavy real page replaces a stub.
//   - m-auth: pages/auth/*. m-home: the project home placeholder.

import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { RootLayout } from '../components/layout/RootLayout.js';
import { AuthedLayout } from '../components/layout/AuthedLayout.js';
import { SlimAuthedLayout } from '../components/layout/SlimAuthedLayout.js';
import { ProjectLayout } from '../components/layout/ProjectLayout.js';
import { AdminLayout } from '../components/layout/AdminLayout.js';
import { FocusedLayout, type FocusedRouteHandle } from '../components/layout/FocusedLayout.js';
import type { ScrollScopeHandle } from '../lib/scrollScope.js';
import { PlatformLayout } from '../components/layout/PlatformLayout.js';
import { RequireSysadmin } from '../components/layout/RequireSysadmin.js';
import { RouteError } from '../components/layout/RouteError.js';
import { ProfileLayout } from '../pages/profile/ProfileLayout.js';
import { ProfileTab } from '../pages/profile/ProfileTab.js';
import { TokensTab } from '../pages/profile/TokensTab.js';
import { RootResolver } from '../pages/RootResolver.js';
import { SelectProjectPage } from '../pages/SelectProjectPage.js';
import { NoProjectsPage } from '../pages/NoProjectsPage.js';
import { ProjectBlockedPage } from '../pages/ProjectBlockedPage.js';
import { NotFoundPage } from '../pages/NotFoundPage.js';
import { ComingSoonPage } from '../pages/ComingSoonPage.js';

const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    // Chrome-less last-resort errorElement for routes not under a layout that
    // provides its own (pre-auth pages, /not-found, the catch-all). Authed +
    // slim pages carry their own errorElement below so the shell chrome stays.
    errorElement: <RouteError />,
    children: [
      // ── pre-auth (no ProtectedRoute) ──────────────────────────────────
      {
        path: '/login',
        lazy: async () => {
          const m = await import('../pages/auth/LoginPage.js');
          return { Component: m.default };
        },
      },
      {
        path: '/change-password',
        lazy: async () => {
          const m = await import('../pages/auth/ChangePasswordPage.js');
          return { Component: m.default };
        },
      },
      {
        path: '/forgot-password',
        lazy: async () => {
          const m = await import('../pages/auth/ForgotPasswordPage.js');
          return { Component: m.default };
        },
      },
      {
        path: '/reset-password',
        lazy: async () => {
          const m = await import('../pages/auth/ResetPasswordPage.js');
          return { Component: m.default };
        },
      },

      // ── authed (ProtectedRoute wraps Outlet via AuthedLayout) ─────────
      {
        element: <AuthedLayout />,
        // Page errors (render crash / lazy chunk-load failure) on an authed page
        // render INSIDE the shell chrome via this pathless wrapper's errorElement
        // — it sits in AuthedLayout's <Outlet>, so the Navbar stays and the user
        // can navigate away. (Putting errorElement on the AuthedLayout route
        // itself would replace the chrome too.) RootLayout's errorElement is the
        // chrome-less fallback for everything outside a layout.
        children: [
          {
            errorElement: <RouteError />,
            children: [
              // authed project-less
              { path: '/', element: <RootResolver /> },
              // The account hub — a tabbed entity surface (the user IS the entity),
              // deep-linkable per tab. APP-143 adds sibling tabs (Security · …).
              {
                path: '/profile',
                element: <ProfileLayout />,
                children: [
                  { index: true, element: <ProfileTab /> },
                  { path: 'tokens', element: <TokensTab /> },
                ],
              },
              // Platform context (sysadmin cross-project, ADR-018), behind the
              // route-level sysadmin guard (the "guard lands with the portal slice"
              // — this is that slice). SSO & Onboarding (ADR-054) is the first REAL
              // platform page; Projects / Users stay ComingSoon stubs until theirs.
              {
                element: <RequireSysadmin />,
                children: [
                  { path: '/platform', element: <Navigate to="/platform/projects" replace /> },
                  // The platform pages render BARE content inside PlatformLayout (the
                  // shared pageBox + pageGutter frame), so /platform/* lives in the same
                  // boxed column as the project pages — one measure, one gutter, wings
                  // on ultra-wide (no per-page max-w wrapper, no nested <main>).
                  {
                    element: <PlatformLayout />,
                    children: [
                      {
                        path: '/platform/projects',
                        lazy: async () => {
                          const m = await import('../pages/platform/projects/PlatformProjectsPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        // `:id` (not `:projectId`) — a `projectId` param here would make
                        // useOptionalProjectParams() treat this platform page as a project
                        // context, rendering the project domain nav + banner alongside the
                        // platform nav (the double-nav bug). Platform ids ride a neutral name.
                        path: '/platform/projects/:id',
                        lazy: async () => {
                          const m = await import('../pages/platform/projects/PlatformProjectDetailPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: '/platform/users',
                        lazy: async () => {
                          const m = await import('../pages/platform/users/PlatformUsersPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: '/platform/sso',
                        lazy: async () => {
                          const m = await import('../pages/platform/sso/SsoProvidersPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: '/platform/sso/:providerId',
                        lazy: async () => {
                          const m = await import('../pages/platform/sso/SsoProviderDetailPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        // The sysadmin catalog import surface (ADR-065) — status +
                        // the two import doors + merge proposals + history.
                        path: '/platform/catalog',
                        lazy: async () => {
                          const m = await import('../pages/platform/catalog/PlatformCatalogPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: '/platform/api-clients',
                        lazy: async () => {
                          const m = await import('../pages/platform/api-clients/ApiClientsListPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        // Neutral :id (not :projectId) — a platform page must not be
                        // treated as project-scoped by useOptionalProjectParams.
                        path: '/platform/api-clients/:id',
                        lazy: async () => {
                          const m = await import('../pages/platform/api-clients/ApiClientDetailPage.js');
                          return { Component: m.default };
                        },
                      },
                    ],
                  },
                  { path: '/platform/*', element: <ComingSoonPage labelKey="gear.platform" /> },
                ],
              },

              // authed project-scoped
              {
                path: '/p/:projectId',
                children: [
                  // domain frame
                  {
                    element: <ProjectLayout />,
                    children: [
                      {
                        index: true,
                        lazy: async () => {
                          const m = await import('../pages/HomePage.js');
                          return { Component: m.default };
                        },
                      },
                      { path: 'monitoring', element: <ComingSoonPage labelKey="domains.monitoring" /> },
                      {
                        path: 'sites',
                        lazy: async () => {
                          const m = await import('../pages/sites/SitesListPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'sites/:siteId',
                        // One scroll scope for the page and its tabs (lib/scrollScope).
                        handle: { scrollScope: 'entity' } satisfies ScrollScopeHandle,
                        lazy: async () => {
                          const m = await import('../pages/sites/SiteDetailPage.js');
                          return { Component: m.default };
                        },
                        children: [
                          {
                            index: true,
                            lazy: async () => {
                              const m = await import('../pages/sites/SiteOverviewTab.js');
                              return { Component: m.SiteOverviewTab };
                            },
                          },
                          {
                            path: 'files',
                            lazy: async () => {
                              const m = await import('../pages/sites/SiteFilesTab.js');
                              return { Component: m.SiteFilesTab };
                            },
                          },
                          {
                            // The site's Tickets tab — the tickets module's list
                            // mounted with its scope pre-bound (peek-only).
                            path: 'tickets',
                            lazy: async () => {
                              const m = await import('../pages/tickets/SiteTicketsTab.js');
                              return { Component: m.SiteTicketsTab };
                            },
                          },
                        ],
                      },
                      // The team's permanent address — in the RAIL-LESS domain
                      // frame like sites/:siteId and assets/:assetId (the area
                      // rail frames LIST surfaces only; an entity's own page
                      // drops it — architect-ruled 2026-08-24, held for the
                      // team page 2026-10-15). The Work rail frames the hub and
                      // the Teams list alone.
                      {
                        path: 'teams/:teamId',
                        handle: { scrollScope: 'entity' } satisfies ScrollScopeHandle,
                        lazy: async () => {
                          const m = await import('../pages/teams/TeamDetailPage.js');
                          return { Component: m.default };
                        },
                      },
                      // The asset's permanent address — in the RAIL-LESS domain
                      // frame like sites/:siteId (the area rail frames LIST
                      // surfaces only; entity pages drop it — architect-ruled
                      // 2026-08-24). Declared AFTER the static `assets/new` +
                      // `assets/import` focused routes (outside this frame).
                      // A LAYOUT route: the header + role-derived tab strip
                      // stay mounted, the tab panels are child routes
                      // (Overview = index · Connectivity on reporters;
                      // Parameters/Usage are direction-tabs with no routes yet).
                      {
                        path: 'assets/:assetId',
                        handle: { scrollScope: 'entity' } satisfies ScrollScopeHandle,
                        lazy: async () => {
                          const m = await import('../pages/assets/AssetDetailPage.js');
                          return { Component: m.default };
                        },
                        children: [
                          {
                            index: true,
                            lazy: async () => {
                              const m = await import('../pages/assets/AssetOverviewTab.js');
                              return { Component: m.default };
                            },
                          },
                          {
                            path: 'files',
                            lazy: async () => {
                              const m = await import('../pages/assets/AssetFilesTab.js');
                              return { Component: m.AssetFilesTab };
                            },
                          },
                          {
                            path: 'connectivity',
                            lazy: async () => {
                              const m = await import('../pages/assets/AssetConnectivityTab.js');
                              return { Component: m.default };
                            },
                          },
                        ],
                      },
                      { path: 'reports', element: <ComingSoonPage labelKey="domains.reports" /> },
                    ],
                  },
                  // The Assets AREA frame — the THIRD pathless frame beside
                  // ProjectLayout/AdminLayout (its rail must sit flush at the
                  // pageBox edge, never inside the domain frame's gutter).
                  // Register is the area landing (an Overview was drafted +
                  // discarded — too thin before reconciliation lands);
                  // siblings join as their modules land.
                  {
                    lazy: async () => {
                      const m = await import('../pages/assets/AssetsAreaLayout.js');
                      return { Component: m.default };
                    },
                    children: [
                      {
                        path: 'assets',
                        lazy: async () => {
                          const m = await import('../pages/assets/AssetRegisterPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        // The Devices ENTRY (ADR-063 §access facet — the face-vs-entry law) — the connectivity record
                        // over the one register (face-vs-entry law); the peek's
                        // device face keys on this route.
                        path: 'assets/devices',
                        lazy: async () => {
                          const m = await import('../pages/assets/AssetDevicesPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'assets/catalog',
                        lazy: async () => {
                          const m = await import('../pages/catalog/CatalogPage.js');
                          return { Component: m.default };
                        },
                      },
                    ],
                  },
                  // The Work AREA frame — the FOURTH pathless frame (the assets
                  // frame's twin): the tickets hub is the area landing; the Teams
                  // beside it (ADR-083 §11); the visits log and planning join as
                  // siblings at their phases.
                  {
                    lazy: async () => {
                      const m = await import('../pages/work/WorkAreaLayout.js');
                      return { Component: m.default };
                    },
                    children: [
                      {
                        path: 'tickets',
                        lazy: async () => {
                          const m = await import('../pages/tickets/TicketsHubPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'teams',
                        lazy: async () => {
                          const m = await import('../pages/teams/TeamsListPage.js');
                          return { Component: m.default };
                        },
                      },
                    ],
                  },
                  // admin frame (pathless — flat URLs, no /admin/ segment)
                  {
                    element: <AdminLayout />,
                    children: [
                      {
                        path: 'users',
                        lazy: async () => {
                          const m = await import('../pages/users/UsersListPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'users/:userId',
                        lazy: async () => {
                          const m = await import('../pages/users/UserDetailPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'roles',
                        lazy: async () => {
                          const m = await import('../pages/roles/RolesListPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'roles/:roleId',
                        lazy: async () => {
                          const m = await import('../pages/roles/RoleDetailPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'responsibilities',
                        lazy: async () => {
                          const m = await import('../pages/admin/responsibilities/ResponsibilitiesListPage.js');
                          return { Component: m.default };
                        },
                      },
                      // Zones & clusters — the two territory levels folded into ONE
                      // tabbed, deep-linkable page (the Site fields fold). Both are
                      // peek-only (ADR-051): list + peek are the whole surface.
                      {
                        path: 'zones-clusters',
                        lazy: async () => {
                          const m = await import('../pages/admin/zones-clusters/ZonesClustersPage.js');
                          return { Component: m.default };
                        },
                      },
                      // AdministrativeAreas — the hierarchical reference-data tree; peek-only.
                      {
                        path: 'administrative-areas',
                        lazy: async () => {
                          const m = await import('../pages/admin/administrative-areas/AdministrativeAreasListPage.js');
                          return { Component: m.default };
                        },
                      },
                      // Asset owners — the pooled books vocabulary (flat; Manage drawer).
                      {
                        path: 'asset-owners',
                        lazy: async () => {
                          const m = await import('../pages/admin/asset-owners/AssetOwnersListPage.js');
                          return { Component: m.default };
                        },
                      },
                      // The four site vocabularies fold into ONE tabbed, deep-linkable
                      // page — Types · Priorities · Categories · Tags as tabs.
                      {
                        path: 'site-fields',
                        lazy: async () => {
                          const m = await import('../pages/admin/site-fields/SiteFieldsPage.js');
                          return { Component: m.default };
                        },
                      },
                      // The Contract section — two sidebar rows: Configurations
                      // (the working collection) and the contract's REFERENCE
                      // surface (terms · bands · commercial flags as tabs on one
                      // deep-linkable page; ?family= implies the Bands tab).
                      {
                        path: 'contract-terms',
                        lazy: async () => {
                          const m = await import('../pages/contract/TermsPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'configurations',
                        lazy: async () => {
                          const m = await import('../pages/contract/ConfigsPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'settings',
                        lazy: async () => {
                          const m = await import('../pages/admin/project-settings/ProjectSettingsPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'settings/files',
                        lazy: async () => {
                          const m = await import('../pages/admin/project-settings/FilesSettingsPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'settings/field',
                        lazy: async () => {
                          const m = await import('../pages/admin/project-settings/FieldSettingsPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'integrations',
                        lazy: async () => {
                          const m = await import('../pages/admin/integrations/IntegrationsPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'audit-log',
                        lazy: async () => {
                          const m = await import('../pages/admin/audit/AuditLogPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'imports',
                        lazy: async () => {
                          const m = await import('../pages/admin/imports/ImportsPage.js');
                          return { Component: m.default };
                        },
                      },
                      {
                        path: 'imports/:jobId',
                        lazy: async () => {
                          const m = await import('../pages/admin/imports/ImportRunPage.js');
                          return { Component: m.default };
                        },
                      },
                      // 'api-docs' is an external link to the backend Swagger UI (/api/docs),
                      // not an SPA route — see config/nav.ts (the api-docs nav item is `external`).
                    ],
                  },
                  // unknown project sub-path → not-found
                  { path: '*', element: <Navigate to="/not-found" replace /> },
                ],
              },
            ],
          },
        ],
      },

      // ── focused chrome-less flows (ProtectedRoute, no nav/sidebar) ───
      //   the user-create wizard (ADR-036 §9 / ADR-038). `users/new` (static)
      //   ranks above the AdminLayout `users/:userId` (dynamic), so it wins here.
      {
        element: <FocusedLayout />,
        errorElement: <RouteError />,
        children: [
          {
            path: '/p/:projectId/users/new',
            handle: { closeTo: (p) => `/p/${p.projectId}/users` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/users/UserCreatePage.js');
              return { Component: m.default };
            },
          },
          // The site-create wizard + its resume surface (ADR-038 draft-backed /
          // ADR-056 §4). `sites/new` (static) out-ranks `sites/:siteId`; the
          // `/setup` leaf out-ranks the detail route by specificity. While a
          // site is a draft, /setup IS its page (the locked draft model).
          {
            path: '/p/:projectId/sites/new',
            handle: { closeTo: (p) => `/p/${p.projectId}/sites` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/sites/SiteCreateWizardPage.js');
              return { Component: m.SiteCreateWizardPage };
            },
          },
          {
            path: '/p/:projectId/sites/:siteId/setup',
            handle: { closeTo: (p) => `/p/${p.projectId}/sites` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/sites/SiteCreateWizardPage.js');
              return { Component: m.SiteSetupWizardPage };
            },
          },
          // The full-page import wizards — one focused route per import family
          // (ADR-059 §9); `?dataset=` preselects the What step. Each static
          // `…/import` leaf out-ranks its family's dynamic detail routes.
          // The contract catalogue has TWO doors into the same wizard — the
          // Configurations list and the Contract-terms reference page — because
          // closing a focused route must return you where you came from. Same
          // component, same three datasets, different closeTo.
          {
            path: '/p/:projectId/configurations/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/configurations` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/contract/CatalogueImportPage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/contract-terms/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/contract-terms` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/contract/CatalogueImportPage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/sites/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/sites` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/sites/SitesImportPage.js');
              return { Component: m.default };
            },
          },
          // The catalog's product add wizard (the deliberate birth door —
          // static, so it outranks nothing; closes onto the catalog list).
          {
            path: '/p/:projectId/assets/catalog/new',
            handle: { closeTo: (p) => `/p/${p.projectId}/assets/catalog` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/catalog/ProductCreatePage.js');
              return { Component: m.default };
            },
          },
          // The asset add wizard (ADR-038 atomic kind — the judged container;
          // `assets/new` static outranks the future `assets/:assetId`).
          {
            path: '/p/:projectId/assets/new',
            handle: { closeTo: (p) => `/p/${p.projectId}/assets` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/assets/AssetCreateWizardPage.js');
              return { Component: m.default };
            },
          },
          // The register page is the asset wizard's close target; the consumables
          // wizard keeps the project-home landing until the consumables
          // transaction verbs repoint it.
          {
            path: '/p/:projectId/assets/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/assets` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/assets/AssetsImportPage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/assets/catalog/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/assets/catalog` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/catalog/CatalogImportPage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/consumables/import',
            handle: { closeTo: (p) => `/p/${p.projectId}` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/consumables/ConsumablesImportPage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/zones/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/zones-clusters` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/admin/zones/ZonesImportPage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/clusters/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/zones-clusters?tab=clusters` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/admin/clusters/ClustersImportPage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/administrative-areas/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/administrative-areas` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/admin/administrative-areas/AdministrativeAreasImportPage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/asset-owners/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/asset-owners` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/admin/asset-owners/AssetOwnersImportPage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/site-types/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/site-fields` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/admin/site-types/SiteTypesImportPage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/site-priorities/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/site-fields?tab=priorities` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/admin/site-priorities/SitePrioritiesImportPage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/site-categories/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/site-fields?tab=categories` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/admin/site-categories/SiteCategoriesImportPage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/site-tags/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/site-fields?tab=tags` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/admin/site-tags/SiteTagsImportPage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/responsibilities/import',
            handle: { closeTo: (p) => `/p/${p.projectId}/responsibilities` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/admin/responsibilities/ResponsibilitiesImportPage.js');
              return { Component: m.default };
            },
          },
          // The contractual-configuration form — the charter-projected heavy
          // editor on its own focused route. Both exits land on
          // the Configurations page. `new` (static) out-ranks `:configId`.
          {
            path: '/p/:projectId/configurations/new',
            handle: { closeTo: (p) => `/p/${p.projectId}/configurations` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/contract/ConfigFormPage.js');
              return { Component: m.ConfigCreatePage };
            },
          },
          {
            path: '/p/:projectId/configurations/:configId/edit',
            handle: { closeTo: (p) => `/p/${p.projectId}/configurations` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/contract/ConfigFormPage.js');
              return { Component: m.ConfigEditPage };
            },
          },
          {
            path: '/p/:projectId/roles/new',
            handle: { closeTo: (p) => `/p/${p.projectId}/roles` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/roles/RoleCreatePage.js');
              return { Component: m.default };
            },
          },
          {
            path: '/p/:projectId/roles/:roleId/capabilities',
            handle: { closeTo: (p) => `/p/${p.projectId}/roles/${p.roleId}` } satisfies FocusedRouteHandle,
            lazy: async () => {
              const m = await import('../pages/roles/RoleCapabilitiesPage.js');
              return { Component: m.default };
            },
          },
          // The platform project-create wizard — chrome-less (FocusedLayout) AND
          // sysadmin-gated (nested RequireSysadmin, since this branch is outside the
          // platform RequireSysadmin block above). `/platform/projects/new` (static)
          // out-ranks the dynamic `/platform/projects/:projectId` detail route.
          {
            element: <RequireSysadmin />,
            children: [
              {
                path: '/platform/projects/new',
            handle: { closeTo: () => '/platform/projects' } satisfies FocusedRouteHandle,
                lazy: async () => {
                  const m = await import('../pages/platform/projects/PlatformProjectCreatePage.js');
                  return { Component: m.default };
                },
              },
            ],
          },
        ],
      },

      // ── authed SLIM pages (no operational tools) ─────────────────────
      //   chooser  — /select-project: lockup brand (cards are the chooser) +
      //              full avatar.
      //   blocked  — /project-blocked: the switcher (aligned with a project page;
      //              the escape hatch) + full avatar.
      //   dead-end — /no-projects: lockup + trimmed avatar (no profile/password).
      {
        element: <SlimAuthedLayout brand="lockup" account="full" />,
        children: [{ path: '/select-project', element: <SelectProjectPage />, errorElement: <RouteError /> }],
      },
      {
        element: <SlimAuthedLayout brand="switcher" account="full" />,
        children: [{ path: '/project-blocked', element: <ProjectBlockedPage />, errorElement: <RouteError /> }],
      },
      {
        element: <SlimAuthedLayout brand="lockup" account="minimal" />,
        children: [{ path: '/no-projects', element: <NoProjectsPage />, errorElement: <RouteError /> }],
      },

      // ── catch-all (pre-auth-safe — no ProtectedRoute) ─────────────────
      { path: '/not-found', element: <NotFoundPage /> },
      { path: '*', element: <Navigate to="/not-found" replace /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
